<script lang="ts">
  import { content } from '../content';
  import { game, type LevelStatus } from '../state/game.svelte';

  interface Props {
    onselect?: () => void;
  }

  let { onselect }: Props = $props();

  const marks: Record<LevelStatus, string> = {
    solved: '✓',
    stale: '↻',
    failing: '✗',
    invalid: '!',
    unchecked: '○',
    unavailable: '·',
  };

  const titles: Record<LevelStatus, string> = {
    solved: 'solved',
    stale: 'solved, but a definition it uses has changed',
    failing: 'does not hold yet',
    invalid: 'something this level needs does not parse',
    unchecked: 'no verdict yet',
    unavailable: 'needs definitions from earlier levels',
  };

  /** What pointing at a row says. A lesson is a page, and has no verdict. */
  function titleOf(level: { id: string; lesson?: boolean }): string {
    return level.lesson === true ? 'a page to read' : titles[game.statusOf(level.id)];
  }

  function select(id: string): void {
    void game.openLevel(id);
    onselect?.();
  }
</script>

<nav class="sidebar" aria-label="Levels">
  {#each content.chapters as chapter (chapter.id)}
    {@const progress = game.chapterProgress(chapter.id)}
    <section>
      <h2>
        {chapter.title}
        {#if progress.solved > 0}
          <span class="progress" class:done={progress.solved === progress.total}>
            {progress.solved}/{progress.total}
          </span>
        {/if}
      </h2>
      <ul>
        {#each chapter.levels as level (level.id)}
          {@const status = game.statusOf(level.id)}
          <li>
            <button
              class="entry {status}"
              class:active={game.activeLevelId === level.id}
              aria-current={game.activeLevelId === level.id ? 'page' : undefined}
              title={titleOf(level)}
              onclick={() => select(level.id)}
            >
              <span class="mark">{level.lesson === true ? '' : marks[status]}</span>
              <span class="id">{level.id}</span>
              <span class="title">{level.title}</span>
            </button>
          </li>
        {/each}
      </ul>
    </section>
  {/each}
</nav>

<style>
  .sidebar {
    overflow-y: auto;
    padding: 12px 8px 40px;
    height: 100%;
  }

  h2 {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-faint);
    margin: 14px 8px 6px;
  }

  .progress {
    margin-left: auto;
    font-family: var(--mono);
    letter-spacing: 0;
    color: var(--text-faint);
  }

  .progress.done {
    color: var(--pass);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .entry {
    display: flex;
    align-items: baseline;
    gap: 7px;
    width: 100%;
    text-align: left;
    background: none;
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 13px;
  }

  .entry:hover {
    background: var(--bg-raised);
  }

  .entry.active {
    background: var(--bg-raised);
    border-color: var(--border-strong);
  }

  .mark {
    width: 1em;
    flex: none;
  }

  .solved .mark {
    color: var(--pass);
  }

  .failing .mark,
  .invalid .mark {
    color: var(--fail);
  }

  .stale .mark {
    color: var(--warn);
  }

  .unchecked .mark,
  .unavailable .mark {
    color: var(--text-faint);
  }

  .id {
    font-family: var(--mono);
    color: var(--text-faint);
    font-size: 12px;
    flex: none;
  }

  .title {
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .unavailable .title {
    color: var(--text-dim);
  }
</style>
